/**
 * ExperienceHeader — editorial header for /upplevelse/:slug pages.
 * Supports optional hero image for visual anchoring.
 */
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

interface ExperienceHeaderProps {
  title: string;
  subtitle: string;
  intro?: string;
  heroImageUrl?: string | null;
}

export default function ExperienceHeader({ title, subtitle, intro, heroImageUrl }: ExperienceHeaderProps) {
  const navigate = useNavigate();

  return (
    <>
      {/* Optional hero image */}
      {heroImageUrl && (
        <div className="relative w-full" style={{ height: 'clamp(200px, 38vh, 400px)' }}>
          <img
            src={heroImageUrl}
            alt={title}
            className="h-full w-full object-cover"
            style={{ filter: 'saturate(0.9) brightness(0.95)' }}
            loading="eager"
          />
          <div
            className="absolute inset-0"
            style={{ background: 'linear-gradient(to top, hsl(var(--background)) 0%, transparent 40%)' }}
          />
        </div>
      )}

      <section className={`container max-w-2xl px-5 ${heroImageUrl ? 'pt-8 sm:pt-10' : 'pt-24 sm:pt-32'} pb-6 sm:pb-8`}>
        <button
          onClick={() => navigate('/')}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors mb-6"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Alla upplevelser
        </button>

        <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight text-foreground">
          {title}
        </h1>

        <p className="mt-1.5 text-base sm:text-lg text-muted-foreground">
          {subtitle}
        </p>

        {intro && (
          <p className="mt-4 max-w-[44ch] text-sm sm:text-base text-foreground/70 leading-relaxed">
            {intro}
          </p>
        )}
      </section>
    </>
  );
}
